import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Hardware for the 3D HUD, so its pieces read as made things: cogs, hex bolts and octagonal bezels.
 * Sizes are CSS pixels like the rest of the HUD. Geometry faces the viewer (+z).
 */

/** A cog: `teeth` square-ish teeth round a rim, an optional hole, extruded `depth` with a small bevel. */
export function cogGeometry(radius: number, teeth: number, toothDepth: number, depth: number, hole = 0): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape(), steps = teeth * 4;
  for (let i = 0; i <= steps; i++) {
    // Each tooth is a quarter rise, a quarter top, a quarter fall and a quarter root.
    const angle = i / steps * Math.PI * 2, phase = i % 4, r = phase === 1 || phase === 2 ? radius + toothDepth : radius;
    const point = new THREE.Vector2(Math.cos(angle) * r, Math.sin(angle) * r);
    if (i === 0) shape.moveTo(point.x, point.y); else shape.lineTo(point.x, point.y);
  }
  if (hole > 0) { const path = new THREE.Path(); path.absarc(0, 0, hole, 0, Math.PI * 2, true); shape.holes.push(path); }
  return new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelSize: .8, bevelThickness: .8, bevelSegments: 1, curveSegments: 24 });
}

/** A regular polygon frame (an octagonal bezel, say): outer and inner radius, flat sides top and bottom. */
export function polygonFrameGeometry(sides: number, outer: number, inner: number, depth: number, bevel = 1.1, bevelSegments = 2): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape(polygonRing(sides, outer));
  if (inner > 0) shape.holes.push(new THREE.Path(polygonRing(sides, inner).reverse()));
  return new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelSize: bevel, bevelThickness: bevel, bevelSegments });
}
/** The corners of a regular polygon of vertex radius `radius`, with flat sides top and bottom. */
function polygonRing(sides: number, radius: number) {
  return Array.from({ length: sides }, (_, i) => { const a = (i + .5) / sides * Math.PI * 2; return new THREE.Vector2(Math.cos(a) * radius, Math.sin(a) * radius); });
}

/** A rectangle `width` by `height` about the origin with its corners cut by `chamfer`, as a closed outline (anticlockwise). */
function chamferedRect(width: number, height: number, chamfer: number) {
  const x = width / 2, y = height / 2, c = Math.min(chamfer, x, y);
  return [[x - c, -y], [x, -y + c], [x, y - c], [x - c, y], [-x + c, y], [-x, y - c], [-x, -y + c], [-x + c, -y]].map(([px, py]) => new THREE.Vector2(px, py));
}
/** A rectangular frame (a portrait's bezel): outer and inner size, corners cut by `chamfer` outside and `chamfer / 2` inside. */
export function rectFrameGeometry(width: number, height: number, innerWidth: number, innerHeight: number, depth: number, chamfer = 4, bevel = 1.1, bevelSegments = 2): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape(chamferedRect(width, height, chamfer));
  if (innerWidth > 0) shape.holes.push(new THREE.Path(chamferedRect(innerWidth, innerHeight, chamfer / 2).reverse()));
  return new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelSize: bevel, bevelThickness: bevel, bevelSegments });
}

/** For a regular octagon with flat sides facing 0°, 45°, 90°…: the vertex radius for an apothem (centre to the middle of a side). */
export const octagonVertex = (apothem: number) => apothem / Math.cos(Math.PI / 8);
/** The angle of the octagon side nearest polar angle `theta`: what a piece set on the outline there faces. */
export const octagonFacing = (theta: number) => Math.round(theta / (Math.PI / 4)) * (Math.PI / 4);
/** The point on an octagon outline (by apothem) at polar angle `theta`. */
export function octagonPoint(apothem: number, theta: number, z = 0) {
  const r = apothem / Math.cos(theta - octagonFacing(theta));
  return new THREE.Vector3(Math.cos(theta) * r, Math.sin(theta) * r, z);
}
/** A tube following the octagon outline from polar angle `from` to `to` (either direction), sharp at its corners. */
export function octagonTubeGeometry(apothem: number, from: number, to: number, radius: number): THREE.TubeGeometry {
  const path = new THREE.CurvePath<THREE.Vector3>(), step = Math.PI / 180, steps = Math.max(1, Math.ceil(Math.abs(to - from) / step));
  let previous = octagonPoint(apothem, from);
  for (let i = 1; i <= steps; i++) { const next = octagonPoint(apothem, from + (to - from) * i / steps); path.add(new THREE.LineCurve3(previous, next)); previous = next; }
  return new THREE.TubeGeometry(path, Math.max(8, steps), radius, 8, false);
}

/** A regular polygon plate (a face or a cup) with flat sides top and bottom; UVs span 0–1 over its bounding square. */
export function polygonGeometry(sides: number, radius: number): THREE.CircleGeometry {
  return new THREE.CircleGeometry(radius, sides, Math.PI / sides);
}

/** A hex bolt head: six-sided, domed a little by its bevel. */
export function boltGeometry(size: number): THREE.CylinderGeometry {
  return new THREE.CylinderGeometry(size, size * 1.05, size * .7, 6).rotateX(Math.PI / 2);
}

/**
 * Several pieces of one material that never move apart, as one geometry: each draw call costs the HUD more than its triangles do.
 * `parts` are geometries and the matrices that place them; the geometries are disposed. Their draw groups are dropped (one material).
 */
export function mergedGeometry(parts: [THREE.BufferGeometry, THREE.Matrix4][]): THREE.BufferGeometry {
  // Merging needs every part indexed or none: mixed, they all go unindexed.
  const mixed = parts.some(([geometry]) => geometry.index) && parts.some(([geometry]) => !geometry.index);
  const placed = parts.map(([geometry, matrix]) => {
    const copy = (mixed && geometry.index ? geometry.toNonIndexed() : geometry.clone()).applyMatrix4(matrix);
    geometry.dispose(); copy.clearGroups(); return copy;
  });
  const merged = mergeGeometries(placed)!; for (const geometry of placed) geometry.dispose();
  return merged;
}
/** A geometry's copies at each of `points`. */
export function copiesGeometry(geometry: THREE.BufferGeometry, points: THREE.Vector3[]): THREE.BufferGeometry {
  const merged = mergedGeometry(points.map(point => [geometry.clone(), new THREE.Matrix4().makeTranslation(point)]));
  geometry.dispose();
  return merged;
}

/** Glows, shadows and glints are light, not substance: a pointer passes through them to the piece underneath. */
export const unpickable = <T extends THREE.Object3D>(object: T): T => { object.raycast = () => {}; return object; };

/** Dark, oiled iron for straps, cogs and bolts. */
export function iron(color: THREE.ColorRepresentation = 0x2b2926): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, metalness: .8, roughness: .42 });
}
